import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  AddressFields,
  cleanAddress,
  emptyAddress,
  validateAddress,
} from "@/components/AddressFields";
import { useAuth } from "@/context/AuthContext";
import { useApiError } from "@/lib/useApiError";

const FIELDS = [
  { name: "bedrijfsnaam", type: "text", required: true, autoComplete: "organization" },
  { name: "kvk", type: "text", required: true, autoComplete: "off" },
  { name: "voornaam", type: "text", required: true, autoComplete: "given-name" },
  { name: "achternaam", type: "text", required: true, autoComplete: "family-name" },
  { name: "telefoon", type: "tel", required: true, autoComplete: "tel" },
  { name: "email", type: "email", required: true, autoComplete: "email" },
  { name: "wachtwoord", type: "password", required: true, autoComplete: "new-password" },
];

const Registreren = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const toMessage = useApiError();

  const [values, setValues] = useState({
    bedrijfsnaam: "",
    kvk: "",
    voornaam: "",
    achternaam: "",
    telefoon: "",
    email: "",
    wachtwoord: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [bezorg, setBezorg] = useState(emptyAddress);
  const [factuur, setFactuur] = useState(emptyAddress);
  const [zelfdeFactuur, setZelfdeFactuur] = useState(true);
  const [adresErrors, setAdresErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setAdres = (soort, veld, waarde) => {
    const setter = soort === "bezorg" ? setBezorg : setFactuur;
    setter((prev) => ({ ...prev, [veld]: waarde }));
    setAdresErrors((prev) => ({ ...prev, [`${soort}.${veld}`]: undefined }));
  };

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errors = {};
    FIELDS.forEach(({ name, required }) => {
      if (required && !values[name].trim()) {
        errors[name] = t("auth.register.errors.required");
      }
    });
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t("auth.register.errors.email");
    }
    if (values.wachtwoord && values.wachtwoord.length < 8) {
      errors.wachtwoord = t("auth.register.errors.password");
    }
    const melding = t("auth.register.errors.required");
    const adresFouten = {
      ...validateAddress("bezorg", bezorg, melding),
      ...(zelfdeFactuur ? {} : validateAddress("factuur", factuur, melding)),
    };
    setAdresErrors(adresFouten);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0 && Object.keys(adresFouten).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setApiError("");
    if (!validate()) return;
    setSubmitting(true);
    const bezorgadres = cleanAddress(bezorg);
    const factuuradres = zelfdeFactuur ? bezorgadres : cleanAddress(factuur);
    try {
      await register({
        email: values.email.trim(),
        wachtwoord: values.wachtwoord,
        bedrijfsnaam: values.bedrijfsnaam.trim(),
        kvk: values.kvk.trim(),
        voornaam: values.voornaam.trim(),
        achternaam: values.achternaam.trim(),
        telefoon: values.telefoon.trim(),
        bezorgadres,
        factuuradres,
        factuuradres_gelijk: zelfdeFactuur,
      });
      navigate("/wachten", { replace: true, state: { justRegistered: true } });
    } catch (error) {
      setApiError(toMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("auth.register.title")} subtitle={t("auth.register.subtitle")} wide testId="register-page">
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          {apiError && (
            <Alert variant="destructive" data-testid="register-error-alert">
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map(({ name, type, autoComplete }) => (
              <div
                key={name}
                className={`space-y-2 ${name === "email" || name === "wachtwoord" ? "sm:col-span-2" : ""}`}
              >
                <Label htmlFor={`register-${name}`}>
                  {t(`auth.register.fields.${name}`)}
                </Label>
                <Input
                  id={`register-${name}`}
                  name={name}
                  type={type}
                  autoComplete={autoComplete}
                  value={values[name]}
                  onChange={(e) => setValue(name, e.target.value)}
                  className={fieldErrors[name] ? "border-destructive" : ""}
                  data-testid={`register-${name}-input`}
                />
                {name === "wachtwoord" && !fieldErrors[name] && (
                  <p className="text-xs text-muted-foreground">
                    {t("auth.register.fields.wachtwoordHint")}
                  </p>
                )}
                {fieldErrors[name] && (
                  <p className="text-sm text-destructive" data-testid={`register-${name}-error`}>
                    {fieldErrors[name]}
                  </p>
                )}
              </div>
            ))}
          </div>

          <Separator />

          <AddressFields
            soort="bezorg"
            idPrefix="register"
            titel={t("auth.register.address.shipping")}
            adres={bezorg}
            errors={adresErrors}
            onChange={setAdres}
          />

          <div className="flex items-center gap-3 rounded-md border bg-secondary/60 p-3">
            <Checkbox
              id="register-same-address"
              checked={zelfdeFactuur}
              onCheckedChange={(v) => setZelfdeFactuur(Boolean(v))}
              data-testid="register-same-address-checkbox"
            />
            <Label htmlFor="register-same-address" className="cursor-pointer text-sm font-normal">
              {t("auth.register.address.same")}
            </Label>
          </div>

          {!zelfdeFactuur && (
            <AddressFields
              soort="factuur"
              idPrefix="register"
              titel={t("auth.register.address.billing")}
              adres={factuur}
              errors={adresErrors}
              onChange={setAdres}
            />
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <Button type="submit" disabled={submitting} data-testid="register-form-submit-button">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitting ? t("auth.register.submitting") : t("auth.register.submit")}
            </Button>
            <p className="text-sm text-muted-foreground">
              {t("auth.register.haveAccount")}{" "}
              <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-login-link">
                {t("nav.login")}
              </Link>
            </p>
          </div>
        </form>
    </AuthShell>
  );
};

export default Registreren;
